<template>
  <div class="book-card" @click="viewDetails">
    <div class="card-background-glow"></div>
    <div class="card-content">
      <div class="cover-container">
        <img 
          :src="coverImage" 
          :alt="book.title" 
          class="book-cover"
          @error="onImageError"
        />
        <div class="cover-bg-shape"></div>
      </div>
      <div class="book-info">
        <h3 class="book-title">{{ book.title }}</h3>
        <p v-if="book.authors.length" class="book-author">{{ book.authors[0].name }}</p>
        <span class="book-category" v-if="book.categories.length">{{ book.categories[0].name }}</span>
      </div>
    </div>
  </div>
</template>

<script>
import { defineComponent, computed, ref } from 'vue';
import { useRouter } from 'vue-router';

export default defineComponent({
  name: 'BookCard',
  props: {
    book: {
      type: Object,
      required: true,
      default: () => ({
        title: '',
        authors: [],
        categories: [],
        cover_image: null,
        is_featured: false
      })
    }
  },
  setup(props) {
    const router = useRouter();
    const imageError = ref(false);
    
    const coverImage = computed(() => {
      if (imageError.value || !props.book.cover_image) {
        // Placeholder image using book title for variety
        return `https://via.placeholder.com/320x480/f3f4f6/4f46e5?text=${encodeURIComponent(props.book?.title || 'Book')}`;
      }
      return props.book.cover_image;
    });
    
    const onImageError = () => {
      imageError.value = true;
    };

    const viewDetails = () => {
      router.push({ name: 'book-detail', params: { id: props.book.id } });
    };
    
    return {
      coverImage,
      onImageError,
      viewDetails
    };
  }
});
</script>

<style lang="scss" scoped>
.book-card {
  position: relative;
  border-radius: var(--border-radius);
  overflow: hidden;
  cursor: pointer;
  transition: transform 0.3s ease, box-shadow 0.3s ease;
  background: var(--card-bg);
  border: 1px solid var(--card-border);
  backdrop-filter: blur(10px);

  &:hover {
    transform: translateY(-8px);
    box-shadow: 0 15px 30px rgba(0, 0, 0, 0.1);
  }
}

.card-content {
  padding: 1.5rem;
  text-align: center;
}

.cover-container {
  position: relative;
  margin-bottom: 1.5rem;

  .book-cover {
    width: 80%;
    aspect-ratio: 2 / 3;
    object-fit: cover;
    border-radius: var(--border-radius-md, 12px);
    position: relative;
    z-index: 2;
    box-shadow: 0 10px 20px rgba(0,0,0,0.15);
  }

  .cover-bg-shape {
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    width: 90%;
    height: 60%;
    background: linear-gradient(45deg, var(--primary-accent), var(--primary-yellow));
    border-radius: 10px;
    z-index: 1;
  }
}

.book-info {
  .book-title {
    font-size: 1.1rem;
    font-weight: 700;
    color: var(--text-color);
    margin-bottom: 0.5rem;
  }
  .book-author {
    color: var(--text-light);
    margin-bottom: 0.5rem;
    font-size: 0.9rem;
  }
  .book-category {
    font-size: 0.8rem;
    color: var(--primary-accent);
    background-color: rgba(255, 167, 38, 0.1);
    padding: 0.25rem 0.75rem;
    border-radius: 20px;
    display: inline-block;
  }
}
</style>